/**
 * Human-readable "code diff" rendering of a PatchAction for the review
 * panel's expandable code block. Pure string building — the caller is
 * responsible for HTML-escaping the result before injecting it into the
 * shadow DOM (action values come from the server and may contain markup).
 */

import type { PatchAction } from '@helpbob/shared';

export function describeAction(action: PatchAction, selector: string): string {
  return [`@ ${selector}`, ...actionLines(action, selector)].join('\n');
}

/**
 * The diff lines of one action, without the selector header. A `compound`
 * (scanner#61) is the lines of its parts, in the order they are applied.
 */
function actionLines(action: PatchAction, selector: string): string[] {
  const lines: string[] = [];

  switch (action.type) {
    case 'setAttribute':
      lines.push(`+ ${action.attribute}="${action.value}"`);
      break;
    case 'removeAttribute':
      lines.push(`- ${action.attribute}`);
      break;
    case 'addAriaLabel':
      lines.push(`+ aria-label="${action.value}"`);
      break;
    case 'addAriaDescribedBy':
      // scanner#368: what the reviewer judges is the TEXT a screen reader will
      // read. `value` is only the id suggested for the source fix — the client
      // never puts it on the page — so it is not shown as if it were markup.
      lines.push(`+ aria-describedby → description: "${action.descriptionText}"`);
      break;
    case 'setRole':
      lines.push(`+ role="${action.role}"`);
      break;
    case 'addClass':
      lines.push(`+ class="… ${action.className}"`);
      break;
    case 'setStyle':
      lines.push(`+ style: ${action.property}: ${action.value}`);
      break;
    case 'wrapElement': {
      const attrs = formatAttributes(action.wrapperAttributes);
      lines.push(`+ <${action.wrapperTag}${attrs}> … </${action.wrapperTag}>`);
      break;
    }
    case 'insertAdjacentHTML':
      lines.push(`+ ${action.html}  (${action.position})`);
      break;
    case 'replaceTag': {
      const attrs = formatAttributes(action.addAttributes);
      lines.push(`~ <…> → <${action.newTag}${attrs}>`);
      for (const removed of action.removeAttributes || []) {
        lines.push(`- ${removed}`);
      }
      break;
    }
    case 'replaceTextContent':
      lines.push(`~ text → "${action.text}"`);
      break;
    case 'injectStyleRule':
      lines.push(`+ <style> ${selector}${action.pseudo || ''} { ${action.declarations} }`);
      break;
    case 'renameId':
      // Paket D (F77): one of two elements sharing the id gets a new one.
      lines.push(`~ id="${action.from}" → id="${action.to}"`);
      break;
    case 'compound':
      for (const part of Array.isArray(action.actions) ? action.actions : []) {
        if (part) lines.push(...actionLines(part, selector));
      }
      break;
  }

  return lines;
}

function formatAttributes(attrs?: Record<string, string>): string {
  if (!attrs) return '';
  return Object.entries(attrs)
    .map(([k, v]) => ` ${k}="${v}"`)
    .join('');
}
